import type { ExternalTaskConcrete } from '../../compliance-shared/types/compliance';

export type HazWasteExternalTask = Extract<
  ExternalTaskConcrete,
  {
    type:
      | 'hazWasteAccumulationDeadline'
      | 'hazWasteStreamReview'
      | 'hazWasteContainerInspection'
      | 'hazWasteTankInspection'
      | 'hazWasteInspectionFinding';
  }
>;

/** The URL of a path on the HazWaste face, which lives on the `hazwaste` subdomain next to the Compliance Tool's. */
export function hazWasteUrl(hazWastePath: string): string {
  return window.location.origin.replace('//compliance', '//hazwaste') + hazWastePath;
}

/** The HazWaste path where the user records the fact that closes the task. */
export function hazWastePathOf(externalTask: HazWasteExternalTask): string {
  switch (externalTask.type) {
    case 'hazWasteAccumulationDeadline':
      return externalTask.deadline.hazWastePath;
    case 'hazWasteStreamReview':
      return externalTask.review.hazWastePath;
    case 'hazWasteContainerInspection':
    case 'hazWasteTankInspection':
      return externalTask.inspection.hazWastePath;
    case 'hazWasteInspectionFinding':
      return externalTask.finding.hazWastePath;
  }
}

/** Explains why a HazWaste-driven task can't be completed in the Compliance Tool. */
export function hazWasteCompletePrompt(type: HazWasteExternalTask['type']): { title: string; message: string } {
  switch (type) {
    case 'hazWasteAccumulationDeadline':
      return {
        title: 'Record the shipment in HazWaste',
        message:
          'This task completes automatically once the container is marked shipped or retired, or is moved ' +
          'out of the central accumulation area, in HazWaste.',
      };
    case 'hazWasteStreamReview':
      return {
        title: 'Complete the review in HazWaste',
        message:
          'This task completes automatically once the waste stream review is completed in HazWaste, where ' +
          'each stream is reviewed and recorded.',
      };
    case 'hazWasteContainerInspection':
      return {
        title: 'Record the inspection in HazWaste',
        message:
          'This task completes automatically once every accumulation area has been inspected on the Inspections ' +
          'page in HazWaste.',
      };
    case 'hazWasteTankInspection':
      return {
        title: 'Record the inspection in HazWaste',
        message:
          "This task completes automatically once the tank's inspection is recorded on the Inspections page in " +
          'HazWaste.',
      };
    case 'hazWasteInspectionFinding':
      return {
        title: 'Record the resolution in HazWaste',
        message:
          'This task completes automatically once what was done to resolve the finding is recorded on the ' +
          'Inspections page in HazWaste.',
      };
  }
}
